<div class="rr-modal-backdrop" *ngIf="open" (click)="close()">
  <div class="rr-modal-card" (click)="$event.stopPropagation()">
    <div class="rr-modal-head">
      <div>
        <h2 class="rr-modal-title">Workflow Actor Roles</h2>
        <p class="rr-modal-sub">
          Manage workflow actor roles that can be assigned as stage actors in workflow templates.
        </p>
      </div>
      <button class="rr-modal-close" type="button" aria-label="Close" (click)="close()">
        <wf-icon name="x" [size]="20"></wf-icon>
      </button>
    </div>

    <div class="rr-modal-body" (scroll)="closeMenusOnScroll()">
      <!-- #1: loader while the role catalog + assigned-group users resolve. -->
      <div class="rr-loading-overlay" *ngIf="loading || usersLoading">
        <span class="rr-spinner" aria-hidden="true"></span>
        <span class="rr-loading-text">Loading roles…</span>
      </div>

      <!-- Toolbar -->
      <div class="rr-toolbar">
        <button class="rr-primary-btn" type="button" (click)="openCreate()">
          <wf-icon name="plus" [size]="16"></wf-icon> New Role
        </button>
        <div class="rr-filter-wrap" (click)="$event.stopPropagation()">
          <button class="rr-ghost-btn" type="button" (click)="toggleFilterMenu($event)"
                  [class.active]="statusFilter !== 'all'">
            <wf-icon name="layout-distribute-horizontal" [size]="16"></wf-icon> Filter
            <span class="rr-filter-dot" *ngIf="statusFilter !== 'all'"></span>
          </button>
          <div class="rr-menu rr-filter-menu" *ngIf="filterMenuOpen">
            <button type="button" [class.sel]="statusFilter === 'all'" (click)="setFilter('all', $event)">
              All roles
            </button>
            <button type="button" [class.sel]="statusFilter === 'active'" (click)="setFilter('active', $event)">
              Active only
            </button>
            <button type="button" [class.sel]="statusFilter === 'inactive'" (click)="setFilter('inactive', $event)">
              Inactive only
            </button>
          </div>
        </div>
      </div>

      <!-- Search -->
      <div class="rr-search">
        <wf-icon name="search" [size]="16"></wf-icon>
        <input type="text" [(ngModel)]="search" placeholder="Search roles" />
      </div>

      <!-- Tenant assignment selector — role→user assignments are scoped per assignment,
           so when a workflow is assigned to more than one account-holder/client pair the
           admin picks which one to configure (otherwise the others are unreachable). -->
      <div class="rr-assignment-select" *ngIf="hasMultipleAssignments">
        <label for="rr-assignment">Configuring assignment</label>
        <select id="rr-assignment" [ngModel]="assignmentId"
                (ngModelChange)="onAssignmentChange($event)">
          <option *ngFor="let a of assignments" [ngValue]="a.workflowAssignmentId">
            {{ assignmentLabel(a) }}
          </option>
        </select>
      </div>

      <!-- Table -->
      <div class="rr-table-scroll">
        <table class="rr-table">
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Description</th>
              <th>Assigned Users</th>
              <th>Status</th>
              <th class="rr-action-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let role of filteredRoles">
              <tr [class.rr-row-inactive]="!role.active" [class.rr-row-open]="isExpanded(role)">
                <td class="rr-role-name">
                  <button type="button" class="rr-caret" [class.open]="isExpanded(role)"
                          [attr.aria-label]="isExpanded(role) ? 'Collapse' : 'Expand'"
                          (click)="toggleExpand(role, $event)">
                    <wf-icon name="chevron-down" [size]="16"></wf-icon>
                  </button>
                  {{ role.label }}
                </td>
                <td class="rr-role-desc">{{ role.description || '—' }}</td>
                <td>
                  <span class="rr-assigned">
                    <wf-icon name="users" [size]="15"></wf-icon> {{ assignedLabel(role) }}
                  </span>
                </td>
                <td>
                  <span class="rr-status" [class.on]="role.active" [class.off]="!role.active">
                    <span class="rr-status-dot"></span>
                    {{ role.active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="rr-action-col">
                  <div class="rr-menu-wrap" (click)="$event.stopPropagation()">
                    <button type="button" class="rr-kebab" aria-label="Row actions"
                            (click)="toggleMenu(role, $event)">
                      <wf-icon name="more-vertical" [size]="18"></wf-icon>
                    </button>
                    <div class="rr-menu rr-menu-fixed" *ngIf="menuRoleId === role.id"
                         [style.top.px]="menuTop" [style.left.px]="menuLeft">
                      <button type="button" (click)="openEdit(role)">
                        <wf-icon name="pencil" [size]="15"></wf-icon> Edit Role
                      </button>
                      <button type="button" (click)="openAssignments(role)">
                        <wf-icon name="users" [size]="15"></wf-icon> Manage User Assignments
                      </button>
                      <div class="rr-menu-sep"></div>
                      <button type="button" [class.danger]="role.active" [class.success]="!role.active"
                              (click)="openConfirm(role)">
                        <wf-icon [name]="role.active ? 'x-circle' : 'check-circle-2'" [size]="15"></wf-icon>
                        {{ role.active ? 'Deactivate Role' : 'Activate Role' }}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
              <tr *ngIf="isExpanded(role)" class="rr-expand-row">
                <td colspan="5">
                  <div class="rr-expand">
                    <div class="rr-expand-title">Assigned users</div>
                    <div class="rr-expand-users" *ngIf="assignedUsers(role).length; else noUsers">
                      <div class="rr-expand-user" *ngFor="let u of assignedUsers(role)">
                        <span class="rr-avatar" [style.background]="avatarColor(u.colorSeed)">{{ initials(u.name) }}</span>
                        <span class="rr-user-meta">
                          <span class="rr-user-name">{{ u.name }}</span>
                          <span class="rr-user-email">{{ u.email }}</span>
                        </span>
                      </div>
                    </div>
                    <ng-template #noUsers>
                      <div class="rr-expand-empty">
                        <wf-icon name="users" [size]="15"></wf-icon> No users assigned to this role
                      </div>
                    </ng-template>
                  </div>
                </td>
              </tr>
            </ng-container>
            <tr *ngIf="filteredRoles.length === 0">
              <td colspan="5" class="rr-empty">No roles match your search.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="rr-showing">
        Showing {{ filteredRoles.length }} of {{ roles.length }} roles
        <span class="rr-showing-counts">· {{ activeCount }} active · {{ inactiveCount }} inactive</span>
      </p>
    </div>

    <div class="rr-modal-foot">
      <button class="rr-done-btn" type="button" (click)="close()">Done</button>
    </div>
  </div>

  <!-- ============ Edit / New role dialog ============ -->
  <div class="rr-sub-backdrop" *ngIf="editOpen" (click)="cancelEdit()">
    <div class="rr-sub-card" (click)="$event.stopPropagation()">
      <div class="rr-sub-head">
        <h3>{{ editMode === 'create' ? 'New Workflow Actor Role' : 'Edit Workflow Actor Role' }}</h3>
        <button class="rr-modal-close" type="button" aria-label="Close" (click)="cancelEdit()">
          <wf-icon name="x" [size]="18"></wf-icon>
        </button>
      </div>
      <div class="rr-sub-body">
        <div class="rr-field">
          <label>Role Name <span class="rr-req">*</span></label>
          <input type="text" [(ngModel)]="editName" maxlength="60" placeholder="e.g. Reviewer"
                 (keyup.enter)="saveEdit()" />
        </div>
        <div class="rr-field">
          <label>Description</label>
          <textarea [(ngModel)]="editDescription" maxlength="200" rows="3"
                    placeholder="What this role is responsible for"></textarea>
        </div>
        <div class="rr-field">
          <label>Status <span class="rr-req">*</span></label>
          <div class="rr-radio-row">
            <label class="rr-radio"><input type="radio" name="editStatus" [value]="true" [(ngModel)]="editActive" /> Active</label>
            <label class="rr-radio"><input type="radio" name="editStatus" [value]="false" [(ngModel)]="editActive" /> Inactive</label>
          </div>
        </div>
        <p class="rr-error" *ngIf="editError">
          <wf-icon name="alert-triangle" [size]="13"></wf-icon> {{ editError }}
        </p>
      </div>
      <div class="rr-sub-foot">
        <button class="rr-cancel-btn" type="button" (click)="cancelEdit()">Cancel</button>
        <button class="rr-done-btn" type="button" (click)="saveEdit()">
          {{ editMode === 'create' ? 'Create Role' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </div>

  <!-- ============ Manage user assignments dialog ============ -->
  <div class="rr-sub-backdrop" *ngIf="assignOpen" (click)="cancelAssignments()">
    <div class="rr-sub-card wide" (click)="$event.stopPropagation()">
      <div class="rr-sub-head">
        <div>
          <h3>Manage User Assignments</h3>
          <p class="rr-sub-sub">Assign users who can act as <strong>{{ assignRole?.label }}</strong>.</p>
        </div>
        <button class="rr-modal-close" type="button" aria-label="Close" (click)="cancelAssignments()">
          <wf-icon name="x" [size]="18"></wf-icon>
        </button>
      </div>
      <div class="rr-sub-body">
        <div class="rr-search">
          <wf-icon name="search" [size]="16"></wf-icon>
          <input type="text" [(ngModel)]="assignSearch" placeholder="Search users by name or email" />
        </div>

        <div class="rr-transfer">
          <!-- Left: all users -->
          <div class="rr-pane">
            <div class="rr-pane-head">All Users ({{ assignFilteredUsers.length }})</div>
            <div class="rr-pane-list">
              <p class="rr-empty" *ngIf="usersLoading">Loading users…</p>
              <p class="rr-empty" *ngIf="!usersLoading && noAssignment">
                Assign groups to this workflow first to choose users.
              </p>
              <label class="rr-user-row" *ngFor="let u of assignFilteredUsers">
                <input type="checkbox" [checked]="isAssigned(u)" (change)="toggleAssigned(u)" />
                <span class="rr-avatar" [style.background]="avatarColor(u.colorSeed)">{{ initials(u.name) }}</span>
                <span class="rr-user-meta">
                  <span class="rr-user-name">{{ u.name }}</span>
                  <span class="rr-user-email">{{ u.email }}</span>
                </span>
              </label>
              <p class="rr-empty" *ngIf="!usersLoading && !noAssignment && assignFilteredUsers.length === 0">No users found.</p>
            </div>
          </div>

          <!-- Transfer arrows -->
          <div class="rr-transfer-arrows">
            <button type="button" title="Add all shown" (click)="selectAllFiltered()">
              <wf-icon name="chevron-down" [size]="18"></wf-icon>
            </button>
            <button type="button" title="Remove all shown" (click)="deselectAllFiltered()">
              <wf-icon name="chevron-down" [size]="18"></wf-icon>
            </button>
          </div>

          <!-- Right: selected users -->
          <div class="rr-pane">
            <div class="rr-pane-head">
              Selected Users ({{ assignSelectedUsers.length }})
              <button type="button" class="rr-clear" *ngIf="assignSelectedUsers.length"
                      (click)="clearAssigned()">Clear all</button>
            </div>
            <div class="rr-pane-list">
              <div class="rr-user-row selected" *ngFor="let u of assignSelectedUsers">
                <span class="rr-avatar" [style.background]="avatarColor(u.colorSeed)">{{ initials(u.name) }}</span>
                <span class="rr-user-meta">
                  <span class="rr-user-name">{{ u.name }}</span>
                  <span class="rr-user-email">{{ u.email }}</span>
                </span>
                <button type="button" class="rr-remove" aria-label="Remove" (click)="toggleAssigned(u)">
                  <wf-icon name="x" [size]="15"></wf-icon>
                </button>
              </div>
              <p class="rr-empty" *ngIf="assignSelectedUsers.length === 0">No users selected yet.</p>
            </div>
          </div>
        </div>
      </div>
      <div class="rr-sub-foot">
        <button class="rr-cancel-btn" type="button" (click)="cancelAssignments()">Cancel</button>
        <button class="rr-done-btn" type="button" [disabled]="assignSaving" (click)="saveAssignments()">
          {{ assignSaving ? 'Saving…' : 'Save Assignments' }}
        </button>
      </div>
    </div>
  </div>

  <!-- ============ Remove-users confirmation (destructive only) ============ -->
  <div class="rr-sub-backdrop" *ngIf="removeConfirmOpen" (click)="cancelRemoveConfirm()">
    <div class="rr-sub-card narrow" (click)="$event.stopPropagation()">
      <div class="rr-sub-head">
        <h3>Remove users?</h3>
        <button class="rr-modal-close" type="button" aria-label="Close" (click)="cancelRemoveConfirm()">
          <wf-icon name="x" [size]="18"></wf-icon>
        </button>
      </div>
      <div class="rr-sub-body">
        <div class="rr-confirm-row">
          <wf-icon class="rr-warn" name="alert-triangle" [size]="22"></wf-icon>
          <p class="rr-confirm-lead">
            This will remove <strong>{{ removeConfirmCount }}</strong>
            {{ removeConfirmCount === 1 ? 'user' : 'users' }} from
            <strong>{{ assignRole?.label }}</strong>. They will no longer act as this role.
          </p>
        </div>
      </div>
      <div class="rr-sub-foot">
        <button class="rr-cancel-btn" type="button" (click)="cancelRemoveConfirm()">Cancel</button>
        <button class="rr-danger-btn" type="button" (click)="confirmSaveAssignments()">Remove &amp; Save</button>
      </div>
    </div>
  </div>

  <!-- ============ Deactivate / activate confirmation dialog ============ -->
  <div class="rr-sub-backdrop" *ngIf="confirmOpen" (click)="cancelConfirm()">
    <div class="rr-sub-card narrow" (click)="$event.stopPropagation()">
      <div class="rr-sub-head">
        <h3>{{ confirmRole?.active ? 'Deactivate Role?' : 'Activate Role?' }}</h3>
        <button class="rr-modal-close" type="button" aria-label="Close" (click)="cancelConfirm()">
          <wf-icon name="x" [size]="18"></wf-icon>
        </button>
      </div>
      <div class="rr-sub-body">
        <div class="rr-confirm-row">
          <wf-icon class="rr-warn" *ngIf="confirmRole?.active" name="alert-triangle" [size]="22"></wf-icon>
          <wf-icon class="rr-ok" *ngIf="!confirmRole?.active" name="check-circle-2" [size]="22"></wf-icon>
          <div>
            <p class="rr-confirm-lead" *ngIf="confirmRole?.active">
              This role is currently used in <strong>{{ templateUsage(confirmRole) }} workflow templates</strong>.
            </p>
            <p class="rr-confirm-lead" *ngIf="!confirmRole?.active">
              Reactivating this role makes it selectable again when assigning stage actors.
            </p>

            <p class="rr-confirm-sub" *ngIf="confirmRole?.active">Deactivating the role will:</p>
            <ul class="rr-confirm-list" *ngIf="confirmRole?.active">
              <li>Keep existing workflow configurations intact</li>
              <li>Mark affected workflows as <strong>Requires Review</strong></li>
              <li>Prevent affected workflows from being published until a replacement role is assigned</li>
            </ul>
          </div>
        </div>
      </div>
      <div class="rr-sub-foot">
        <button class="rr-cancel-btn" type="button" (click)="cancelConfirm()">Cancel</button>
        <button type="button" [class.rr-danger-btn]="confirmRole?.active"
                [class.rr-success-btn]="!confirmRole?.active" (click)="confirmToggle()">
          {{ confirmRole?.active ? 'Deactivate Role' : 'Activate Role' }}
        </button>
      </div>
    </div>
  </div>

  <app-success-toast *ngIf="toastService.showToast"></app-success-toast>
</div>
